import React, { createContext, useState, useEffect, useRef } from 'react'
import { authService, type AdminOnboardingPayload, type AdminOnboardingResult, type AuthenticationResponse, type AuthenticationState, type MfaEnrollmentStartResponse } from '../services/auth'
import { getAccessContext } from '../services/access'
import { getApiAuthToken, setApiAuthToken, setApiTenantContext } from '../services/api'

export interface User {
  id: string
  email: string
  role: string
  permissions: string[]
}

export interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  authenticationState: AuthenticationState | null
  login: (email: string, password: string) => Promise<AuthenticationResponse>
  verifyMfaChallenge: (challengeToken: string, code: string) => Promise<AuthenticationResponse>
  startMfaEnrollment: () => Promise<MfaEnrollmentStartResponse>
  confirmMfaEnrollment: (code: string) => Promise<AuthenticationResponse>
  acknowledgeMfaRecoveryCodes: () => Promise<void>
  hasPendingMfaRecoveryCodes: () => boolean
  getPendingMfaRecoveryCodes: () => string[]
  signup: (email: string, password: string) => Promise<void>
  registerAdminOnboarding: (payload: AdminOnboardingPayload) => Promise<AdminOnboardingResult>
  completeAdminOnboarding: (result: AdminOnboardingResult) => void
  logout: () => Promise<void>
  isAuthenticated: boolean
}

export const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [authenticationState, setAuthenticationState] = useState<AuthenticationState | null>(null)
  const pendingEnrollmentCompletion = useRef<AuthenticationResponse | null>(null)
  const sessionRestoreGeneration = useRef(0)

  const establishAuthenticatedUser = async (responseData: any, fallbackEmail = '') => {
    if (responseData?.token) setApiAuthToken(responseData.token)
    const sessionUser = responseData?.user || responseData?.profile
    const context = await getAccessContext()
    const membershipType = context.tenantMemberships?.find(membership => membership.tenant_id === context.tenantId)?.membership_type
    setUser({
      id: context.currentUser?.id || sessionUser?.id || fallbackEmail,
      email: context.currentUser?.email || sessionUser?.email || fallbackEmail,
      role: context.isSystemSuperAdmin
        ? 'system_super_admin'
        : membershipType === 'tenant_admin'
        ? 'admin'
        : membershipType === 'corporate_user'
          ? 'corporate'
          : membershipType === 'worker' ? 'worker' : sessionUser?.role || 'worker',
      permissions: context.effectivePermissionKeys || sessionUser?.permissions || [],
    })
    setAuthenticationState('authenticated')
  }

  useEffect(() => {
    // StrictMode replay, unmount and an explicit identity transition retire old probes.
    const generation = ++sessionRestoreGeneration.current
    const isCurrent = () => sessionRestoreGeneration.current === generation
    // Session restoration distinguishes three states up front:
    //  1. Bearer token present -> this is a token-backed session; restore via
    //     the access context and skip the cookie probe entirely. Calling
    //     /auth/session here would always 401 (no cookie was ever set for
    //     this login path) and that 401 is expected noise, not a real error.
    //  2. No token -> probe the cookie-backed /auth/session. A 401 here means
    //     "no session", not a failure worth logging.
    //  3. Neither restores -> show no authenticated user. Preserve credentials
    //     on transport/server failure so a later reload can retry verification.
    const restoreFromToken = async () => {
      try {
        const context = await getAccessContext()
        if (!isCurrent()) return
        const email = context.currentUser?.email || ''
        const membershipType = context.tenantMemberships?.find(membership => membership.tenant_id === context.tenantId)?.membership_type
        setUser({
          id: context.currentUser?.id || email || 'admin',
          email,
          role: context.isSystemSuperAdmin
            ? 'system_super_admin'
            : membershipType === 'tenant_admin'
            ? 'admin'
            : membershipType === 'corporate_user'
              ? 'corporate'
              : 'worker',
          permissions: context.effectivePermissionKeys || [],
        })
      } catch (contextError: any) {
        if (!isCurrent()) return
        try {
          const mfa = await authService.getMfaStatus()
          if (!isCurrent()) return
          if (mfa.data?.assurance === 'mfa_enrollment_restricted') {
            setAuthenticationState('mfa_enrollment_required')
            setUser(null)
            return
          }
        } catch (sessionError: any) {
          if (!isCurrent()) return
          // Only a canonical authentication rejection proves this credential
          // unusable. A deployment outage or tenant denial must not erase it.
          if (sessionError.response?.status === 401) {
            setApiAuthToken(null)
            setError(null)
            setUser(null)
            return
          }
        }
        if (!isCurrent()) return
        setError(contextError.response?.data?.message || 'Session could not be restored. Reload to retry when the service is available.')
        setUser(null)
      }
    }

    const restoreFromCookie = async () => {
      try {
        const response = await authService.getSession()
        if (!isCurrent()) return
        if (response.data?.authenticationState === 'mfa_enrollment_required') {
          setAuthenticationState('mfa_enrollment_required')
          setUser(null)
        } else {
          setUser(response.data.user)
        }
      } catch (sessionError: any) {
        if (!isCurrent()) return
        if (sessionError.response?.status !== 401) {
          setError('Session could not be restored. Reload to retry when the service is available.')
        }
        setUser(null)
      }
    }

    const checkSession = async () => {
      try {
        if (getApiAuthToken()) {
          await restoreFromToken()
        } else {
          await restoreFromCookie()
        }
      } finally {
        if (isCurrent()) setLoading(false)
      }
    }

    void checkSession()
    return () => {
      if (isCurrent()) sessionRestoreGeneration.current++
    }
  }, [])

  const login = async (email: string, password: string): Promise<AuthenticationResponse> => {
    sessionRestoreGeneration.current++
    setLoading(true)
    setError(null)
    try {
      setApiAuthToken(null)
      setApiTenantContext(null)
      pendingEnrollmentCompletion.current = null
      const response = await authService.login(email, password)
      const state = response.data?.authenticationState || 'authenticated'
      setAuthenticationState(state)
      if (state === 'mfa_required') {
        return response.data
      }
      if (response.data?.token) setApiAuthToken(response.data.token)
      if (state === 'mfa_enrollment_required') {
        setUser(null)
        return response.data
      }
      await establishAuthenticatedUser(response.data, email)
      return response.data
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data?.error?.message || 'Login failed'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const verifyMfaChallenge = async (challengeToken: string, code: string) => {
    setLoading(true)
    setError(null)
    try {
      const response = await authService.verifyMfaChallenge(challengeToken, code)
      await establishAuthenticatedUser(response.data)
      return response.data
    } catch (err: any) {
      const message = err.response?.data?.message || err.response?.data?.error?.message || 'MFA verification failed'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const clearExpiredEnrollmentSession = () => {
    setApiAuthToken(null)
    setApiTenantContext(null)
    pendingEnrollmentCompletion.current = null
    setAuthenticationState(null)
    setUser(null)
  }
  const startMfaEnrollment = async (): Promise<MfaEnrollmentStartResponse> => {
    try { return (await authService.startMfaEnrollment()).data }
    catch (err: any) {
      if (err.response?.status === 401) clearExpiredEnrollmentSession()
      throw err
    }
  }

  const confirmMfaEnrollment = async (code: string) => {
    setLoading(true)
    setError(null)
    try {
      const response = await authService.confirmMfaEnrollment(code)
      if (response.data?.token) setApiAuthToken(response.data.token)
      pendingEnrollmentCompletion.current = response.data
      return response.data
    } catch (err: any) {
      if (err.response?.status === 401) clearExpiredEnrollmentSession()
      const message = err.response?.data?.message || err.response?.data?.error?.message || 'MFA enrollment failed'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const acknowledgeMfaRecoveryCodes = async () => {
    const completion = pendingEnrollmentCompletion.current
    if (!completion) throw new Error('No MFA enrollment completion is pending.')
    await establishAuthenticatedUser(completion)
    pendingEnrollmentCompletion.current = null
  }

  const signup = async (email: string, password: string) => {
    sessionRestoreGeneration.current++
    setLoading(true)
    setError(null)
    try {
      const response = await authService.signup(email, password)
      setUser(response.data.user)
    } catch (err: any) {
      const message = err.response?.data?.message || 'Signup failed'
      setError(message)
      throw err
    } finally {
      setLoading(false)
    }
  }

  const registerAdminOnboarding = async (payload: AdminOnboardingPayload): Promise<AdminOnboardingResult> => {
    sessionRestoreGeneration.current++
    setError(null)
    try {
      const response = await authService.registerAdminOnboarding(payload)
      const result: AdminOnboardingResult = { ...response.data, accessContext: null, accessContextError: null }
      if (result.token) {
        setApiAuthToken(result.token)
        setApiTenantContext(null)
        const state = result.authenticationState || (result.restricted ? 'mfa_enrollment_required' : 'authenticated')
        setAuthenticationState(state)
        if (state !== 'authenticated') { setUser(null); return result }
        try {
          result.accessContext = await getAccessContext()
        } catch (contextError: any) {
          result.accessContextError = contextError.response?.data?.message || 'Access context is not ready yet.'
        }
      }
      return result
    } catch (err: any) {
      const message = err.response?.data?.message || 'Signup failed'
      setError(message)
      throw err
    }
  }

  const completeAdminOnboarding = (result: AdminOnboardingResult) => {
    sessionRestoreGeneration.current++
    if (result.restricted || result.authenticationState === 'mfa_enrollment_required') return
    const context = result.accessContext
    const contextUser = context?.currentUser
    const profile = result.profile
    const email = contextUser?.email || profile?.email || ''
    setUser({
      id: contextUser?.id || result.userId || email || 'admin',
      email,
      role: profile?.role || (context?.tenantMemberships?.[0]?.membership_type === 'tenant_admin' ? 'admin' : 'admin'),
      permissions: context?.effectivePermissionKeys || [],
    })
  }

  const logout = async () => {
    sessionRestoreGeneration.current++
    try {
      await authService.logout()
      setApiAuthToken(null)
      setApiTenantContext(null)
      pendingEnrollmentCompletion.current = null
      setUser(null)
      setAuthenticationState(null)
    } catch (err) {
      console.error('Logout failed', err)
    }
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        error,
        authenticationState,
        login,
        verifyMfaChallenge,
        startMfaEnrollment,
        confirmMfaEnrollment,
        acknowledgeMfaRecoveryCodes,
        hasPendingMfaRecoveryCodes: () => pendingEnrollmentCompletion.current !== null,
        getPendingMfaRecoveryCodes: () => pendingEnrollmentCompletion.current?.recoveryCodes || [],
        signup,
        registerAdminOnboarding,
        completeAdminOnboarding,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = React.useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used within AuthProvider')
  return context
}
